import { useTheme } from '@mui/material'
import type { LoopEvent } from '@tunasong/schemas'
import { isLoopEvent } from '@tunasong/schemas'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import invariant from 'tiny-invariant'
import type { RendererPlugin } from '../renderer.js'
import { LoopBody, LoopHandleLeft, LoopHandleRight, Loop as StyledLoop } from './styles.js'

const MIN_LOOP_WIDTH = 5 // Minimum width of the loop in seconds

/** Render a loop marker */
const Loop: RendererPlugin = ({ laneHeight, event, pixelsPerSecond, onUpdateEvent, onEventClick }) => {
  const theme = useTheme()

  const [loop, setLoop] = useState<LoopEvent>()

  const [isDragging, setIsDragging] = useState(false)
  const mode = useRef<'start' | 'end' | 'move'>(undefined)

  const handleBeginMove = useCallback((ev: React.SyntheticEvent) => {
    ev.preventDefault()
    ev.stopPropagation()
    mode.current = 'move'
    setIsDragging(true)
  }, [])

  const handleBeginStart = useCallback((ev: React.SyntheticEvent) => {
    ev.preventDefault()
    ev.stopPropagation()
    mode.current = 'start'
    setIsDragging(true)
  }, [])

  const handleBeginEnd = useCallback((ev: React.SyntheticEvent) => {
    ev.preventDefault()
    ev.stopPropagation()
    mode.current = 'end'
    setIsDragging(true)
  }, [])

  /** Keep track of updates to avoid resetting loop on useEffect */
  const hasUpdatedRef = useRef(false)
  const handleMouseUp = useCallback(
    (ev: MouseEvent) => {
      if (!isDragging || !loop) {
        return
      }
      ev.preventDefault()
      ev.stopPropagation()
      setIsDragging(false)
      mode.current = undefined

      if (onUpdateEvent) {
        hasUpdatedRef.current = true
        onUpdateEvent({
          ...loop,
          enabled: true,
        })
      }
    },
    [isDragging, loop, onUpdateEvent]
  )

  const handleEventClick = (ev: React.MouseEvent) => {
    if (!onEventClick) {
      return
    }
    const loopEvent = loop ?? event
    onEventClick(ev, { ...loopEvent, enabled: true } as LoopEvent)
  }

  const handleMouseMove = useCallback(
    (ev: MouseEvent) => {
      invariant(isLoopEvent(loop), 'Loop renderer must be used with LoopEvent')

      const curMode = mode.current
      if (!(isDragging && curMode && onUpdateEvent)) {
        return
      }
      const deltaSecs = ev.movementX / pixelsPerSecond
      const start = ['start', 'move'].includes(curMode)
        ? Math.min(loop.end - MIN_LOOP_WIDTH, Math.max(loop.start + deltaSecs, 0))
        : loop?.start
      const end = ['end', 'move'].includes(curMode)
        ? Math.max(loop.start + MIN_LOOP_WIDTH, (loop.end ?? 0) + deltaSecs, 0)
        : (loop?.end ?? 0)
      const payload = {
        ...loop,
        start,
        end,
      } satisfies LoopEvent
      setLoop(payload)
    },
    [isDragging, loop, onUpdateEvent, pixelsPerSecond]
  )
  useEffect(() => {
    /** These change, and we need to remove the same ones */
    if (!isDragging) {
      return
    }
    // document.addEventListener('click', handleMouseUp)
    document.addEventListener('mouseup', handleMouseUp)
    document.addEventListener('mousemove', handleMouseMove)
    return () => {
      // document.removeEventListener('click', handleMouseUp)
      document.removeEventListener('mouseup', handleMouseUp)
      document.removeEventListener('mousemove', handleMouseMove)
    }
  }, [handleMouseMove, handleMouseUp, isDragging])

  useEffect(() => {
    if (isDragging) {
      return
    }
    if (!isLoopEvent(event)) {
      return
    }
    if (!hasUpdatedRef.current) {
      setLoop(event)
    }
    hasUpdatedRef.current = false
  }, [event, isDragging, loop])

  if (!loop) {
    return null
  }

  const x1 = loop.start * pixelsPerSecond
  const x2 = loop.end * pixelsPerSecond
  const width = x2 - x1
  const y = 0
  const height = laneHeight / 1.5
  const fill = loop.enabled
    ? theme.vars.palette.primary.dark
    : isDragging
      ? theme.vars.palette.secondary.dark
      : theme.vars.palette.grey['500']
  const handleWidth = 16

  const startPoly = `${x1},${y} ${x1},${laneHeight} ${x1 + handleWidth},${y}`
  const endPoly = `${x2},${y} ${x2},${laneHeight} ${x2 - handleWidth},${y}`

  return (
    <StyledLoop onClick={handleEventClick}>
      <title>Loop {event.name}</title>
      <LoopHandleLeft as="polygon" points={startPoly} fill={fill} onMouseDown={handleBeginStart} />
      <LoopBody x={x1} y={y} width={width} height={height} fill={fill} onMouseDown={handleBeginMove} />
      {laneHeight > 10 && width > 20 ? (
        <text
          x={x1 + width / 2}
          y={y + 6}
          textAnchor="middle"
          alignmentBaseline="middle"
          fill={'white'}
          fontSize={laneHeight - 4}
          pointerEvents="none"
          style={{ userSelect: 'none' }}
        >
          {(() => {
            const fontSize = laneHeight - 4
            // Estimate average char width as 0.6 * fontSize
            const avgCharWidth = 0.6 * fontSize
            const maxChars = Math.floor(width / avgCharWidth)
            if (event.name.length <= maxChars) {
              return event.name
            }
            if (maxChars <= 3) {
              return '...'
            }
            return event.name.slice(0, maxChars - 3) + '...'
          })()}
        </text>
      ) : null}

      <LoopHandleRight as="polygon" points={endPoly} fill={fill} onMouseDown={handleBeginEnd} />
    </StyledLoop>
  )
}

export default Loop
